import React from "react";
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
} from "@mui/material";
import { MUIFlatTableColumn } from "../../components/universal_mui_table/MUIFlatTable";
import { getFormattedCell } from "../../utils/mui_table_utils";
import { useTheme } from "@mui/material/styles";

export interface FlatTableProps {
  columns: MUIFlatTableColumn[];
  getRow: (rowIndex: number) => Record<string, any>;
  rowCount: number;
  showHeader?: boolean;
  size?: "small" | "medium";
  elevation?: number;
  minWidth?: number | string;
}

export const FlatTable: React.FC<FlatTableProps> = ({
  columns,
  getRow,
  rowCount,
  showHeader = true,
  size = "small",
  elevation = 1,
  minWidth = 400,
}) => {
  const theme = useTheme();

  // Calculate left offset for sticky columns
  const getStickyLeftOffset = (columnIndex: number) => {
    let offset = 0;
    for (let i = 0; i < columnIndex; i++) {
      if (columns[i].sticky) {
        offset += Number(columns[i].width) || 0;
      }
    }
    return offset;
  };

  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <Paper elevation={elevation} sx={{ overflow: "hidden" }}>
        <Table size={size} sx={{ minWidth }}>
          {showHeader && (
            <TableHead>
              <TableRow>
                {columns.map((column, index) => (
                  <TableCell
                    key={column.key}
                    align={column.align || "left"}
                    sx={{
                      backgroundColor: "#f5f5f5", // Grey header background
                      height: "48px", // Same as MUIFlatTable headers
                      verticalAlign: "middle",
                      fontWeight: "bold",
                      width: column.width,
                      minWidth: column.width,
                      ...(column.sticky && {
                        position: "sticky",
                        top: 0,
                        left: getStickyLeftOffset(index),
                        backgroundColor: "#f5f5f5", // Grey background for sticky columns too
                      }),
                    }}
                  >
                    {(column as any).label || column.key}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {Array.from({ length: rowCount }, (_, rowIndex) => {
              const row = getRow(rowIndex);
              return (
                <TableRow key={rowIndex}>
                  {columns.map((column, index) => {
                    // Use renderCell if provided, otherwise use default formatting
                    const cellContent = column.renderCell
                      ? column.renderCell(row, rowIndex)
                      : getFormattedCell({
                          value: row[column.key],
                          col: column.key,
                          columnFormatters: {},
                          row,
                          isDelta: false,
                          theme,
                          columnType: column.cellType,
                          headerType: column.headerType,
                          shadeHistory: false,
                        });

                    return (
                      <TableCell
                        key={column.key}
                        align={column.align || "left"}
                        sx={{
                          p: 0, // No padding - same as MUIFlatTable
                          height: "40px", // Fixed height for consistent row heights
                          verticalAlign: "middle",
                          width: column.width,
                          minWidth: column.width,
                          backgroundColor: "white", // White background like MUIFlatTable
                          ...(column.sticky && {
                            position: "sticky",
                            backgroundColor: "white",
                            left: getStickyLeftOffset(index),
                          }),
                        }}
                      >
                        {cellContent}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};
